import { Link } from 'react-router-dom';
import useProjectDetails from './useProjectDetails';
import { Tabs, TabPanel } from '@round/ui-kit';
import styles from './ProjectDetails.module.css';
import InstagramCampaigns from './InstagramCampaigns/InstagramCampaigns';
import YoutubeCampaigns from './YoutubeCampaigns/YoutubeCampaigns';
import usePlatformHashSelect, {
    DefaultPlatformSelectorFunction,
    getDefaultPlatform,
} from '../../hooks/usePlatformHashSelect';
import AggregatedPlatformStats from './PlatformHeadlineStats/PlatformHeadlineStats';
import { useCallback, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import capitalize from 'lodash/capitalize';
import PlatformTabs from 'Modules/Plans/components/PlatformTabs/PlatformTabs';
import useUrlState from 'Hooks/useUrlState';
import SongDataModal from 'Modules/Plans/Songs/SongDataModal/SongDataModal';
import useClientSelect from 'Modules/Plans/Clients/hooks/useClientSelect';
import TikTokTab from './Tabs/TikTokTab/TikTokTab';
import ProjectData from './ProjectData/ProjectData';
import { assets } from '@round/whitelabel-ui';

const { ChevronDownFilled: ChevronDownFilledIcon } = assets;

type UrlState = {
    song: string;
};

const ProjectDetails = () => {
    const [urlState, setUrlState] = useUrlState<Partial<UrlState>>();

    const { project, status, team } = useProjectDetails();
    const { selectedClientId, setSelectedClientId } = useClientSelect();

    useEffect(() => {
        if (team?.client_id && team.client_id !== selectedClientId) {
            setSelectedClientId(team.client_id);
        }
    }, [selectedClientId, setSelectedClientId, team?.client_id]);

    const [selectedPlatform, setSelectedPlatform] = usePlatformHashSelect(
        useCallback<DefaultPlatformSelectorFunction>(
            (goToPlatform) => {
                if (status !== 'success') {
                    return;
                }

                goToPlatform(getDefaultPlatform(project?.platforms ?? []), true);
            },
            [project?.platforms, status]
        )
    );

    const helmetTitle = project?.name
        ? `${project.name}${selectedPlatform ? ` · ${capitalize(selectedPlatform)}` : ''}`
        : null;

    return (
        <>
            <Tabs activeTab={selectedPlatform} onChange={setSelectedPlatform}>
                <Helmet>{helmetTitle && <title>{helmetTitle}</title>}</Helmet>

                <header className={styles.header}>
                    <div className={styles.goBackContainer}>
                        <Link className={styles.goBackButton} to="/c/projects/">
                            <ChevronDownFilledIcon className={styles.goBackButtonIcon} />
                            Projects
                        </Link>
                    </div>

                    <ProjectData
                        platform={selectedPlatform}
                        onSongClick={(songId) => setUrlState({ song: songId.toString() })}
                    />

                    <PlatformTabs className={styles.tabsNav}>
                        <PlatformTabs.Tab name="instagram" platform="instagram" />
                        <PlatformTabs.Tab name="tiktok" platform="tiktok" />
                        <PlatformTabs.Tab name="youtube" platform="youtube" />
                    </PlatformTabs>
                </header>

                <TabPanel name="instagram" data-draggable-container>
                    <AggregatedPlatformStats platform="instagram" />
                    <InstagramCampaigns />
                </TabPanel>

                <TabPanel name="tiktok" data-draggable-container>
                    <TikTokTab />
                </TabPanel>

                <TabPanel name="youtube" data-draggable-container>
                    <AggregatedPlatformStats platform="youtube" />
                    <YoutubeCampaigns />
                </TabPanel>
            </Tabs>

            <SongDataModal
                key={urlState.song}
                songId={urlState.song ? Number(urlState.song) : undefined}
                isOpen={!!urlState.song}
                onClose={() => {
                    setUrlState({ song: undefined });
                }}
            />
        </>
    );
};

export default ProjectDetails;
